import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let sid = sessionStorage.getItem('_a_sid');
  if (!sid) {
    sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem('_a_sid', sid);
  }
  return sid;
}

function getDeviceInfo() {
  const ua = navigator.userAgent;
  let type = 'desktop';
  if (/Mobi|Android/i.test(ua)) type = 'mobile';
  else if (/Tablet|iPad/i.test(ua)) type = 'tablet';

  let browser = 'other';
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = 'Safari';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Edg\//.test(ua)) browser = 'Edge';

  let os = 'other';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';

  return {
    type, browser, os,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
  };
}

// Attempt lightweight geo from timezone
// Geo data resolved once per session via our own IP API
let _geoData = null;
let _geoFetched = false;

async function fetchGeo() {
  if (_geoFetched) return _geoData;
  _geoFetched = true;
  try {
    const cached = sessionStorage.getItem('_a_geo');
    if (cached) { _geoData = JSON.parse(cached); return _geoData; }
    const resp = await axios.get(`${API}/analytics/locate`);
    _geoData = resp.data || {};
    sessionStorage.setItem('_a_geo', JSON.stringify(_geoData));
  } catch { _geoData = {}; }
  return _geoData;
}

function getGeoHint() {
  // Synchronous fallback — enriched with server-side geo once available
  const base = { timezone: '', language: navigator.language || '' };
  try { base.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_e) { /* noop */ }
  if (_geoData) {
    return { ...base, country: _geoData.country || '', region: _geoData.region || '', city: _geoData.city || '' };
  }
  return base;
}

const queue = [];
let flushTimer = null;

function flushQueue() {
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  axios.post(`${API}/analytics/track`, batch, { headers }).catch(() => {});
}

function enqueue(event) {
  queue.push(event);
  if (flushTimer) clearTimeout(flushTimer);
  if (queue.length >= 10) {
    flushQueue();
  } else {
    flushTimer = setTimeout(flushQueue, 3000);
  }
}

export function trackAction(action, metadata = {}) {
  enqueue({
    event_type: 'action',
    session_id: getSessionId(),
    page: window.location.pathname,
    action,
    metadata,
    device: getDeviceInfo(),
    referrer: document.referrer,
    timestamp: new Date().toISOString(),
  });
}

export function useAnalytics() {
  const location = useLocation();
  const lastPage = useRef('');

  // Fetch geo data once per session on mount
  useEffect(() => { fetchGeo(); }, []);

  // Track page views on route changes
  useEffect(() => {
    const path = location.pathname;
    if (path === lastPage.current) return;
    lastPage.current = path;

    const geo = getGeoHint();
    enqueue({
      event_type: 'page_view',
      session_id: getSessionId(),
      page: path,
      metadata: { ...geo, search: location.search },
      device: getDeviceInfo(),
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
    });
  }, [location]);

  // Track clicks on interactive elements
  useEffect(() => {
    const handler = (e) => {
      const el = e.target.closest('[data-testid], button, a[href]');
      if (!el) return;

      const testId = el.getAttribute('data-testid') || '';
      const tag = el.tagName.toLowerCase();
      const text = (el.textContent || '').trim().slice(0, 60);
      const href = el.getAttribute('href') || '';

      // Skip tracking analytics-internal clicks and minor elements
      if (testId.startsWith('analytics-')) return;

      enqueue({
        event_type: 'click',
        session_id: getSessionId(),
        page: window.location.pathname,
        element: testId || `${tag}:${text}`,
        metadata: { tag, href: href.slice(0, 200) },
        device: getDeviceInfo(),
        timestamp: new Date().toISOString(),
      });
    };

    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, []);

  // Flush on page unload
  useEffect(() => {
    const onUnload = () => flushQueue();
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);

  const trackEvent = useCallback((action, metadata = {}) => {
    trackAction(action, metadata);
  }, []);

  return { trackEvent };
}
